import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _Minus, _Checkmark;
const _excluded = ["id", "checked", "defaultChecked", "size", "error", "indeterminate", "label", "onChange", "rootRef", "className", "tabIndex"];
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Checkmark, Minus } from '@resultadosdigitais/tangram-react-icons';
import { ScopedCssBaseline, FocusStyle } from '../StyleBase';
import InputBase from '../InputBase';
import { useFormControl, FormControlLabel } from '../Form';
import { SIZES } from '../helper';
import { useControlled, useId } from '../hooks';
const resolveStyle = (checked, disabled, error) => {
  // disabled style
  if (disabled) {
    return css(["svg{fill:var(--neutral-icon-low-emphasis);}&,&:hover{background-color:var(--neutral-surface-disabled);border-color:var(--neutral-border-interactive-disabled);}"]);
  }

  // checked style (default)
  if (checked) {
    return css(["&,&:hover{background-color:var(--primary-surface-high-emphasis);border-color:var(--primary-surface-high-emphasis);}"]);
  }

  // invalid style
  if (error) {
    return css(["&,&:hover{background-color:var(--neutral-surface);border-color:var(--danger-border);}"]);
  }
  return null;
};
const Input = styled(InputBase).withConfig({
  displayName: "Checkbox__Input",
  componentId: "sc-c8x4vk-0"
})(["cursor:inherit;position:absolute;opacity:0;top:0;left:0;"]);
const Wrapper = styled.span.withConfig({
  displayName: "Checkbox__Wrapper",
  componentId: "sc-c8x4vk-1"
})(["", " border-radius:var(--border-radius-rounded);border:1px solid var(--neutral-border-interactive);display:inline-flex;justify-content:center;align-items:center;vertical-align:middle;position:relative;margin:0;margin-top:2px;cursor:pointer;background-color:var(--neutral-surface);&:hover{background-color:var(--neutral-surface);border-color:var(--neutral-border-interactive-hover);}svg{fill:var(--neutral-icon-high-emphasis);}", " ", ";"], ScopedCssBaseline, _ref => {
  let {
    size
  } = _ref;
  const currentSize = size === SIZES.MD ? '18px' : '24px';
  return css(["width:", ";min-width:", ";height:", ";svg{width:14px;height:14px;}"], currentSize, currentSize, currentSize);
}, _ref2 => {
  let {
    checked,
    disabled,
    error
  } = _ref2;
  return resolveStyle(checked, disabled, error);
});
const Root = styled.label.withConfig({
  displayName: "Checkbox__Root",
  componentId: "sc-c8x4vk-2"
})(["display:flex;align-items:flex-start;cursor:pointer;", ""], FocusStyle);

/**
 * Note: we don't use the `required` props of context for checkbox, once it is applied can be a trouble for multiples elements.
 * It must be controlled outside.
 */
const Checkbox = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
      id: idProp,
      checked,
      defaultChecked,
      size,
      error,
      indeterminate,
      label,
      onChange,
      rootRef,
      className,
      tabIndex
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  const id = useId(idProp);
  const {
    disabled: disabledFormControl,
    required: requiredFormControl
  } = useFormControl();
  const [value, setValue] = useControlled({
    value: checked,
    defaultValue: Boolean(defaultChecked)
  });
  const disabled = disabledFormControl || rest.disabled;
  const required = requiredFormControl || rest.required;
  const props = {
    error,
    disabled,
    checked: value
  };
  const handleInputChange = event => {
    event.persist();
    const newStatus = event.target.checked;
    setValue(newStatus);
    onChange === null || onChange === void 0 ? void 0 : onChange(event, newStatus);
  };
  const getCheckIcon = () => value && (indeterminate ? _Minus || (_Minus = /*#__PURE__*/React.createElement(Minus, {
    title: "input indeterminate",
    titleId: "indeterminate",
    fill: "var(--neutral-icon-high-emphasis)"
  })) : _Checkmark || (_Checkmark = /*#__PURE__*/React.createElement(Checkmark, {
    title: "input checked",
    titleId: "checked",
    fill: "var(--neutral-icon-high-emphasis)"
  })));
  return /*#__PURE__*/React.createElement(Root, {
    ref: rootRef,
    className: className,
    disabled: disabled,
    tabIndex: tabIndex
  }, /*#__PURE__*/React.createElement(Wrapper, _extends({
    size: size
  }, props), getCheckIcon(), /*#__PURE__*/React.createElement(Input, _extends({
    id: id,
    type: "checkbox",
    onChange: handleInputChange,
    "aria-checked": value,
    "aria-disabled": disabled,
    "aria-invalid": error,
    "aria-required": required,
    ref: ref,
    tabIndex: tabIndex
  }, props, rest))), label && /*#__PURE__*/React.createElement(FormControlLabel, _extends({
    htmlFor: id
  }, props), label));
});
Checkbox.displayName = 'Checkbox';
Checkbox.style = Root;
Checkbox.sizes = {
  md: SIZES.MD,
  lg: SIZES.LG
};
Checkbox.defaultProps = {
  size: Checkbox.sizes.md,
  indeterminate: false,
  disabled: false,
  error: false,
  rootRef: null,
  onChange: () => {}
};
process.env.NODE_ENV !== "production" ? Checkbox.propTypes = {
  /**
   * Defines the `Checkbox` state. Used for **Controlled** component.
   */
  checked: PropTypes.bool,
  /**
   * Sets the `Checkbox` checked by default. Used for **Uncontrolled** component.
   */
  defaultChecked: PropTypes.bool,
  /**
   * Disables the `Checkbox`.
   */
  disabled: PropTypes.bool,
  /**
   * Sets the id.
   */
  id: PropTypes.string,
  /**
   * Switch `Checkbox` icon between checkmark and indeterminate.
   */
  indeterminate: PropTypes.bool,
  /**
   * Sets error style.
   */
  error: PropTypes.bool,
  /**
   * Sets the label.
   */
  label: PropTypes.node,
  /**
   * Callback fired when the pagination changes.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   * @param {boolean} newStatus - The new status about if is checked.
   */
  onChange: PropTypes.func,
  /**
   * Pass a ref to the element wrapper.
   */
  rootRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /**
   * Sets the `Checkbox` size.
   */
  size: PropTypes.oneOf([Checkbox.sizes.md, Checkbox.sizes.lg])
} : void 0;
export default Checkbox;